import { useCallback, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import { useHubBasePath } from "../../../utils/hubPaths";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { useAdminAppMode } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { ChatAvatar } from "./ChatThread";
import cs from "./chat.module.css";
import md from "./manageDesktop.module.css";

function formatPreviewTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function InboxRow({
  to,
  state,
  photoUrl,
  name,
  preview,
  time,
  unread,
  club,
  group,
  navLink = false,
}) {
  const body = (
    <>
      <ChatAvatar
        photoUrl={photoUrl}
        name={name}
        club={club}
        group={group}
        className={club ? cs.chatInboxAvatarClub : cs.chatInboxAvatar}
        imgClassName={cs.chatInboxAvatarImg}
      />
      <span className={cs.chatInboxBody}>
        <span className={cs.chatInboxTop}>
          <span className={cs.chatInboxName}>{name}</span>
          <span className={cs.chatInboxTime}>{formatPreviewTime(time)}</span>
        </span>
        <span className={cs.chatInboxPreview}>{preview || "Нет сообщений"}</span>
      </span>
      {unread > 0 ? (
        <span className={cs.chatInboxBadge}>
          {unread > 99 ? "99+" : unread}
        </span>
      ) : null}
    </>
  );

  if (navLink) {
    return (
      <li>
        <NavLink
          to={to}
          state={state}
          className={({ isActive }) =>
            isActive
              ? `${cs.chatInboxRow} ${cs.chatInboxRowActive}`
              : cs.chatInboxRow
          }
        >
          {body}
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <Link to={to} state={state} className={cs.chatInboxRow}>
        {body}
      </Link>
    </li>
  );
}

function sortThreadsByActivity(threads) {
  return [...threads].sort((a, b) => {
    const ta = a.last_at ? new Date(a.last_at).getTime() : 0;
    const tb = b.last_at ? new Date(b.last_at).getTime() : 0;
    return tb - ta;
  });
}

export default function ChatInboxHub({ portal = "staff", embedded = false }) {
  const hubBase = useHubBasePath();
  const clientBase = "/app/chat";
  const { tenant } = useSelector((state) => state.auth);
  const { athleteQueryParam, activeAthlete, isParentMode } = useClientAthlete();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdminApp = useAdminAppMode();
  const isMobile = useIsMobile(768);
  const isManageDesktop = portal === "staff" && isAdminApp && !isMobile;
  const isSidebar = embedded || isManageDesktop;
  const useNavLinks = embedded;

  const athleteId = athleteQueryParam?.athlete_id ?? "";

  const load = useCallback(async () => {
    try {
      if (portal === "client") {
        const athleteParams = athleteId ? { athlete_id: athleteId } : {};
        const data = await clinicAPI.getClientChatInbox(athleteParams);
        const rawThreads = (data.threads || []).map((t) => {
          if (t.thread_type !== "direct") return t;
          return {
            ...t,
            client_name:
              isParentMode && activeAthlete?.full_name
                ? activeAthlete.full_name
                : t.client_name || "Тренер и администрация",
          };
        });
        setThreads(sortThreadsByActivity(rawThreads));
      } else {
        const data = await clinicAPI.getStaffChatInbox();
        setThreads(sortThreadsByActivity(data.threads || []));
      }
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [portal, athleteId, isParentMode, activeAthlete?.full_name]);

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 2000);
    const onActivity = () => load();
    window.addEventListener("baysport:chat-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:chat-activity", onActivity);
    };
  }, [load]);

  const title = portal === "client" ? "Сообщения" : "Чат";

  const rowProps = useNavLinks ? { navLink: true } : {};

  const listBody = loading ? (
        <div className={cs.chatInboxLoading}>
          <div className={cs.chatLoadingDots}>
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : threads.length === 0 ? (
        <div className={cs.chatInboxEmpty}>
          <p className={cs.chatEmptyTitle}>Пока нет переписок</p>
          <p className={cs.chatEmptyHint}>
            {portal === "client"
              ? "Напишите в чат клуба или тренеру"
              : "Сообщения от клиентов появятся здесь"}
          </p>
        </div>
      ) : (
        <ul className={cs.chatInboxList}>
          {threads.map((thread) => {
            if (thread.thread_type === "club") {
              const clubTo =
                portal === "client" ? `${clientBase}/club` : `${hubBase}/chat/club`;
              return (
                <InboxRow
                  key="club"
                  to={clubTo}
                  club
                  name={thread.client_name || "Чат клуба"}
                  preview={thread.last_body}
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...rowProps}
                />
              );
            }

            if (thread.thread_type === "group") {
              const groupTo =
                portal === "client"
                  ? `${clientBase}/group/${thread.group_id}`
                  : `${hubBase}/chat/group/${thread.group_id}`;
              return (
                <InboxRow
                  key={thread.group_id}
                  to={groupTo}
                  state={{ name: thread.client_name }}
                  group
                  name={thread.client_name}
                  preview={thread.last_body || "Чат тренировочной группы"}
                  time={thread.last_at}
                  unread={thread.unread_count}
                  {...rowProps}
                />
              );
            }

            if (portal === "client" && thread.thread_type === "member") {
              return (
                <InboxRow
                  key={thread.peer_id}
                  to={`${clientBase}/member/${thread.peer_id}`}
                  state={{ name: thread.client_name }}
                  photoUrl={thread.client_photo_url}
                  name={thread.client_name}
                  preview={thread.last_body || "Личные сообщения"}
                  time={thread.last_at}
                  unread={thread.unread_count}
                />
              );
            }

            if (portal === "client" && thread.thread_type === "direct") {
              return (
                <InboxRow
                  key="direct"
                  to={`${clientBase}/personal`}
                  name={thread.client_name}
                  preview={
                    thread.last_body ||
                    "Личные сообщения тренеру и администрации"
                  }
                  time={thread.last_at}
                  unread={thread.unread_count}
                />
              );
            }

            return (
              <InboxRow
                key={thread.client_id || `direct-${thread.client_name}`}
                to={
                  useNavLinks
                    ? `${hubBase}/chat/client/${thread.client_id}`
                    : `${hubBase}/clients/${thread.client_id}/chat`
                }
                state={{
                  fromChatHub: true,
                  clientName: thread.client_name,
                }}
                photoUrl={thread.client_photo_url}
                name={thread.client_name}
                preview={thread.last_body}
                time={thread.last_at}
                unread={thread.unread_count}
                {...rowProps}
              />
            );
          })}
        </ul>
      );

  if (isSidebar) {
    return (
      <div className={`${cs.chatHubPage} ${md.desktopChatSidebarInner}`}>
        {embedded ? (
          <header className={md.desktopChatSidebarHead}>
            <h2 className={md.desktopChatSidebarTitle}>{title}</h2>
          </header>
        ) : null}
        {listBody}
      </div>
    );
  }

  return (
    <div className={cs.chatHubPage}>
      <header className={cs.chatHubHeader}>
        <h1 className={cs.chatHubTitle}>{title}</h1>
        {portal === "client" && tenant?.name ? (
          <p className={cs.chatHubSub}>{tenant.name}</p>
        ) : null}
      </header>
      {listBody}
    </div>
  );
}
